import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Kbd } from "./kbd";

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "flex w-full min-h-0 flex-col overflow-hidden bg-transparent text-text",
        className,
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = "Search",
  description = "Search across your workspace...",
  children,
  className,
  shouldFilter,
  footer,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  shouldFilter?: boolean;
  /** Optional hint row pinned under the list (↑↓ Navigate · ↵ Open · Esc Close). */
  footer?: React.ReactNode;
}) {
  return (
    <Dialog {...props}>
      <DialogContent
        className={cn(
          "top-[15vh] max-w-xl translate-y-0 gap-0 overflow-hidden rounded-xl p-0",
          className,
        )}
        showCloseButton={false}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Command shouldFilter={shouldFilter}>{children}</Command>
        {footer ? <CommandFooter>{footer}</CommandFooter> : null}
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  size = "default",
  trailing,
  ...props
  // `size` shadows the native `<input size>` (a character count nobody sets
  // here) so the row can be sized by name rather than by class string.
}: Omit<React.ComponentProps<typeof CommandPrimitive.Input>, "size"> & {
  /**
   * `"default"` is the 44px palette row; `"sm"` is the `h-control-lg` search
   * row a `Picker` panel uses (`docs/picker-taxonomy.md` §3.2).
   */
  size?: "default" | "sm";
  /**
   * End-of-row slot. Exists for the `Picker`'s loading spinner, which §3.2
   * places inside the search row rather than over the list — a spinner in the
   * list body would replace the results the user is still reading.
   */
  trailing?: React.ReactNode;
}) {
  const sm = size === "sm";
  return (
    <div
      data-slot="command-input-wrapper"
      data-size={size}
      className={cn(
        "flex shrink-0 items-center gap-2",
        sm ? "h-control-lg px-4" : "h-11 px-4",
      )}
    >
      <SearchIcon
        className={cn(
          "shrink-0 text-text-tertiary",
          sm ? "size-3.5" : "size-4",
        )}
      />
      <CommandPrimitive.Input
        data-slot="command-input"
        // opero-app paints every `<input>` with a fill (`index.css`, the
        // borderless contract) via an unlayered rule that outranks
        // `bg-transparent`. The search row is not a well — it is the header of
        // the palette/picker surface — so it takes the documented opt-out.
        data-bare-input=""
        className={cn(
          "h-full w-full bg-transparent text-sm text-text outline-none",
          "placeholder:text-text-tertiary",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
      {trailing}
    </div>
  );
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "max-h-80 overflow-y-auto overflow-x-hidden overscroll-contain p-1 scroll-py-1",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn("py-8 text-center text-xs text-text-tertiary", className)}
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "overflow-hidden text-text",
        "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2",
        "[&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold",
        "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide",
        "[&_[cmdk-group-heading]]:text-text-tertiary",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      // Last-resort line — see `DropdownMenuSeparator`.
      className={cn("-mx-1 my-1 h-px bg-line-subtle", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  marker = true,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item> & {
  /**
   * The 2px accent bar on the cursor row. On by default for the command
   * palette, where the bar *is* the only selection signal.
   *
   * A `Picker` sets `marker={false}`: its rows also carry a trailing check for
   * the chosen value, and next to that check the bar reads as a second,
   * contradictory selection signal (`docs/picker-taxonomy.md` §3.2). The
   * `data-[selected=true]:bg-selected` wash still shows the cursor.
   */
  marker?: boolean;
}) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex h-control cursor-default select-none items-center gap-2",
        "rounded-sm px-2 text-sm text-text outline-none",
        "transition-[background-color,color] duration-100 ease-out-fast",
        // 2px accent marker on the selected row — opacity-toggled so the row
        // never shifts as the cmdk cursor moves.
        marker && [
          "before:absolute before:left-0 before:top-1/2 before:h-4 before:w-0.5",
          "before:-translate-y-1/2 before:rounded-full before:bg-accent before:opacity-0",
          "data-[selected=true]:before:opacity-100",
        ],
        "data-[selected=true]:bg-selected",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0",
        "[&_svg:not([class*='size-'])]:size-3.5",
        "[&_svg:not([class*='text-'])]:text-text-tertiary",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Right-aligned trailing hint on a command row.
 *
 * - `keys` renders proper keyboard caps (`<Kbd/>`-style).
 * - `children` renders plain tertiary meta text (entity type, category, …).
 */
function CommandShortcut({
  className,
  keys,
  children,
  ...props
}: React.ComponentProps<"span"> & { keys?: string | string[] }) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "ml-auto flex shrink-0 items-center gap-0.5 pl-3",
        keys === undefined && "text-xs font-medium text-text-tertiary",
        className,
      )}
      {...props}
    >
      {keys === undefined ? children : <Kbd keys={keys} />}
    </span>
  );
}

/** Hint row pinned to the bottom of the palette. */
function CommandFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="command-footer"
      // `bg-sunken` (same call as `SheetFooter`): the palette is the overlay
      // tier, so the hint row recedes rather than rising, and space alone is too
      // weak to hold a 32px strip of tertiary text apart from the last result.
      className={cn(
        "flex h-8 shrink-0 items-center gap-3 bg-sunken px-4 text-xs text-text-tertiary",
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
  CommandFooter,
};
